import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import SEO from '../components/SEO';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2 } from 'lucide-react';
import { businessInfo } from '../data/mock';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    propertyType: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ContactPage",
        "name": "Contact Pierce Premier Cleaning Services",
        "description": "Get a free cleaning quote in Brevard County, Florida"
      },
      {
        "@type": "LocalBusiness",
        "name": "Pierce Premier Cleaning Services",
        "telephone": "+1-321-408-1347"
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://pierce-premier.preview.emergentagent.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Contact"
          }
        ]
      }
    ]
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock form submission
    console.log('Quote Request Submitted:', formData);
    
    // Show success state
    setIsSubmitted(true);
    toast.success('Quote request submitted successfully! We\'ll contact you within 24 hours.');
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        phone: '',
        serviceType: '',
        propertyType: '',
        message: ''
      });
      setIsSubmitted(false);
    }, 3000);
  };

  return (
    <>
      <SEO 
        title="Contact Us | Request a Quote | Pierce Premier Cleaning Services"
        description="Get a free cleaning quote in Brevard County. Call 321-408-1347 or fill out our contact form for personalized service from Pierce Premier Cleaning Services."
        canonical="/contact"
        structuredData={structuredData}
      />
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-cyan-50 via-blue-50 to-purple-50 py-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-7xl text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Get Your Free Quote
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Ready for a cleaner space? Contact us today and we'll provide a personalized quote for your needs.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Get in Touch
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  Have questions or ready to schedule a cleaning? We're here to help! Reach out through any of the methods below.
                </p>
              </div>

              {/* Contact Cards */}
              <div className="space-y-4">
                <Card className="hover:shadow-lg transition-shadow border-2 hover:border-cyan-200">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Phone className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                        <a href="tel:3212151101" className="text-cyan-600 hover:text-cyan-700 text-lg font-medium">
                          {businessInfo.phone}
                        </a>
                        <p className="text-sm text-gray-500 mt-1">Mon-Fri: 8AM-6PM, Sat: 9AM-4PM</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-lg transition-shadow border-2 hover:border-cyan-200">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                        <a href={`mailto:${businessInfo.email}`} className="text-cyan-600 hover:text-cyan-700 break-all">
                          {businessInfo.email}
                        </a>
                        <p className="text-sm text-gray-500 mt-1">We respond within 24 hours</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-lg transition-shadow border-2 hover:border-cyan-200">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Service Area</h3>
                        {businessInfo.serviceArea.map((area, idx) => (
                          <p key={idx} className="text-gray-600">{area}</p>
                        ))}
                        <p className="text-sm text-gray-500 mt-1">Proudly serving the Space Coast</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-lg transition-shadow border-2 hover:border-cyan-200">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-pink-400 to-rose-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Clock className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Business Hours</h3>
                        <div className="space-y-1 text-gray-600">
                          <p>Monday - Friday: {businessInfo.hours.weekdays}</p>
                          <p>Saturday: {businessInfo.hours.saturday}</p>
                          <p>Sunday: {businessInfo.hours.sunday}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Why Choose Us */}
              <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Why Choose Us?
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Free, no-obligation quotes</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Same-day response guaranteed</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Flexible scheduling options</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">100% satisfaction guarantee</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Quote Request Form */}
            <div>
              <Card className="border-2 shadow-xl">
                <CardContent className="p-8">
                  {!isSubmitted ? (
                    <>
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">
                        Request a Free Quote
                      </h2>
                      <p className="text-gray-600 mb-6">
                        Fill out the form below and we'll get back to you within 24 hours
                      </p>

                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                          <Label htmlFor="name">Full Name *</Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="John Smith"
                            required
                            className="border-gray-300"
                          />
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="email">Email *</Label>
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              placeholder="john@example.com"
                              required
                              className="border-gray-300"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="phone">Phone *</Label>
                            <Input
                              id="phone"
                              name="phone"
                              type="tel"
                              value={formData.phone}
                              onChange={handleInputChange}
                              placeholder="(321) 555-0123"
                              required
                              className="border-gray-300"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="serviceType">Service Type *</Label>
                          <Select
                            value={formData.serviceType}
                            onValueChange={(value) => handleSelectChange('serviceType', value)}
                            required
                          >
                            <SelectTrigger className="border-gray-300">
                              <SelectValue placeholder="Select a service" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="residential">Residential Cleaning</SelectItem>
                              <SelectItem value="commercial">Commercial Cleaning</SelectItem>
                              <SelectItem value="deep-cleaning">Deep Cleaning</SelectItem>
                              <SelectItem value="move-cleaning">Move-In/Move-Out</SelectItem>
                              <SelectItem value="window">Window Cleaning</SelectItem>
                              <SelectItem value="post-construction">Post-Construction</SelectItem>
                              <SelectItem value="senior-checkin">Senior Home Check-In</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="propertyType">Property Type *</Label>
                          <Select
                            value={formData.propertyType}
                            onValueChange={(value) => handleSelectChange('propertyType', value)}
                            required
                          >
                            <SelectTrigger className="border-gray-300">
                              <SelectValue placeholder="Select property type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="house">House</SelectItem>
                              <SelectItem value="apartment">Apartment</SelectItem>
                              <SelectItem value="condo">Condo</SelectItem>
                              <SelectItem value="vacation-home">Vacation Home</SelectItem>
                              <SelectItem value="office">Office</SelectItem>
                              <SelectItem value="retail">Retail Space</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="message">Additional Details</Label>
                          <Textarea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            placeholder="Tell us about your cleaning needs, property size, preferred schedule, etc."
                            rows={5}
                            className="border-gray-300 resize-none"
                          />
                        </div>

                        <Button
                          type="submit"
                          size="lg"
                          className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white text-lg py-6"
                        >
                          <Send className="w-5 h-5 mr-2" />
                          Request Free Quote
                        </Button>

                        <p className="text-xs text-gray-500 text-center">
                          By submitting this form, you agree to be contacted by Pierce Premier Cleaning Services regarding your quote request.
                        </p>
                      </form>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle2 className="w-10 h-10 text-green-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        Thank You!
                      </h3>
                      <p className="text-gray-600 mb-4">
                        Your quote request has been submitted successfully.
                      </p>
                      <p className="text-gray-500 text-sm">
                        We'll contact you within 24 hours to discuss your cleaning needs.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default ContactPage;
